import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/api'; // Assuming your API service is here
import { differenceInDays } from 'date-fns'; // For easy date calculations
import { logger } from '../utils/logger';

interface SubscriptionInfo {
  hasSubscription: boolean;
  stripeCustomerId: string | null;
  seatsUsed?: number;
  seatLimit?: number;
  accountsUsed?: number;
  accountLimit?: number;
  simUsed?: number;
  monthlySimQuota?: number;
  quotaResetAt?: string | null; // ISO date string
  trialEndsAt?: string | null; // ISO date string
  plan?: string; // e.g., 'TRIAL', 'STARTER', 'PRO', 'INTERNAL'
  status?: string; // Stripe subscription status e.g., 'active', 'past_due'
  planName?: string; // Product name from Stripe e.g., "Starter Plan"
  organizationStatus?: string; // Organization's own status e.g., 'ACTIVE', 'PAST_DUE'
  userRole?: string; // e.g., 'ADMIN', 'USER'
  error?: string;
}

interface QuotaContextType {
  subscriptionInfo: SubscriptionInfo | null;
  isLoading: boolean;
  error: string | null;
  isBannerDismissed: boolean;
  dismissBanner: () => void;
  fetchSubscriptionInfo: () => Promise<void>;
  daysUntilQuotaReset: number | null;
  simulationsLeft: number | null;
  showQuotaBanner: boolean;
}

const QuotaContext = createContext<QuotaContextType | undefined>(undefined);

const QUOTA_BANNER_DISMISSED_KEY = 'quotaBannerDismissed';

export const QuotaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState<boolean>(() => {
    return sessionStorage.getItem(QUOTA_BANNER_DISMISSED_KEY) === 'true';
  });

  const fetchSubscriptionInfo = useCallback(async () => {
    // User authentication is handled via cookies
    setIsLoading(true);
    try {
      const data = await apiService.getSubscriptionInfo(); // This should now return userRole too
      setSubscriptionInfo(data);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch subscription information';
      setError(errorMessage);
      setSubscriptionInfo(null); // Clear previous data on error
      // Only log error if it's not an authentication issue (which is expected when not logged in)
      if (!errorMessage.includes('Authentication token not found')) {
        logger.error("Error fetching subscription info for QuotaContext:", errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionInfo();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSubscriptionInfo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSubscriptionInfo]);

  const dismissBanner = () => {
    sessionStorage.setItem(QUOTA_BANNER_DISMISSED_KEY, 'true');
    setIsBannerDismissed(true);
  };

  const daysUntilQuotaReset = React.useMemo(() => {
    if (subscriptionInfo?.quotaResetAt) {
      const now = new Date();
      const lastResetDate = new Date(subscriptionInfo.quotaResetAt);
      
      let nextResetDate = new Date(lastResetDate);
      nextResetDate.setMonth(now.getMonth()); // Start with current month

      // If last reset day is, e.g., 28th, and current month is Feb, 
      // date-fns will handle setting it to the last day of Feb if we set day then month.
      // It's safer to setMonth, then setDate to avoid issues with months with fewer days.
      nextResetDate.setDate(lastResetDate.getDate());

      if (now >= nextResetDate) {
        // If current date is past this month's reset day, or it IS today (meaning it should have reset or will reset next month)
        // move to next month
        nextResetDate.setMonth(nextResetDate.getMonth() + 1);
        // Setting day again in case the next month has fewer days (e.g. March 31 -> April 30)
        nextResetDate.setDate(lastResetDate.getDate()); 
      }
      
      // Ensure the calculated nextResetDate is valid and in the future
      if (nextResetDate > now) {
        return differenceInDays(nextResetDate, now);
      }
      // If for some reason calculation results in past/present, it might indicate an issue or an immediate reset.
      // For banner display, showing 0 days or null is fine if it's today or past.
      // Let's return 0 if it's today and supposed to reset.
      if (differenceInDays(nextResetDate, now) === 0) return 0;
    }
    return null;
  }, [subscriptionInfo?.quotaResetAt]);

  const simulationsLeft = React.useMemo(() => {
    if (typeof subscriptionInfo?.simUsed === 'number' && typeof subscriptionInfo?.monthlySimQuota === 'number') {
      return Math.max(0, subscriptionInfo.monthlySimQuota - subscriptionInfo.simUsed);
    }
    return null;
  }, [subscriptionInfo?.simUsed, subscriptionInfo?.monthlySimQuota]);

  const showQuotaBanner = React.useMemo(() => {
    if (!subscriptionInfo || isLoading || isBannerDismissed) return false;
    if (subscriptionInfo.userRole !== 'ADMIN') return false;
    if (subscriptionInfo.plan === 'TRIAL' || subscriptionInfo.plan === 'INTERNAL') return false;
    if (typeof subscriptionInfo.simUsed !== 'number' || typeof subscriptionInfo.monthlySimQuota !== 'number' || subscriptionInfo.monthlySimQuota === 0) return false;
    
    const usagePercentage = (subscriptionInfo.simUsed / subscriptionInfo.monthlySimQuota) * 100;
    return usagePercentage >= 80;
  }, [subscriptionInfo, isLoading, isBannerDismissed]);

  return (
    <QuotaContext.Provider value={{
      subscriptionInfo,
      isLoading,
      error,
      isBannerDismissed,
      dismissBanner,
      fetchSubscriptionInfo,
      daysUntilQuotaReset,
      simulationsLeft,
      showQuotaBanner
    }}>
      {children}
    </QuotaContext.Provider>
  );
};

export const useQuota = (): QuotaContextType => {
  const context = useContext(QuotaContext);
  if (context === undefined) {
    throw new Error('useQuota must be used within a QuotaProvider');
  }
  return context;
}; 